import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';

const ALL_OPPORTUNITY_DEALS_FETCH_LIMIT = 500;

export const offerQueryKeys = {
  all: ['offers'] as const,
  details: () => [...offerQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...offerQueryKeys.details(), id] as const,
  opportunityDeals: (id: string | undefined) => [...offerQueryKeys.all, 'opportunity', id, 'deals'] as const,
  opportunityRelated: (id: string | undefined) => [...offerQueryKeys.all, 'opportunity', id, 'related'] as const,
  opportunity: (id: string) => [...offerQueryKeys.all, 'opportunity', id] as const,
  salesforceCampaignSchedules: (id: string | undefined) =>
    [...offerQueryKeys.all, id, 'salesforce-campaign-schedules'] as const,
};

export type OpportunityRoomAsset = {
  account__name?: string | null;
  accountid?: string | null;
  additional_bedding_information__c?: string | null;
  additional_bedding_type__c?: string | null;
  child_bedding__c?: string | null;
  cot_provided__c?: string | null;
  createddate?: string | null;
  createdbyid?: string | null;
  createdby__name?: string | null;
  current_opp__c?: string | null;
  currencyisocode?: string | null;
  id?: string | number | null;
  included_number_of_adults__c?: string | number | null;
  included_number_of_children__c?: string | number | null;
  included_number_of_infant__c?: string | number | null;
  included_number_of_teenagers__c?: string | number | null;
  last_validated_on__c?: string | null;
  lastmodifieddate?: string | null;
  lastmodifiedbyid?: string | null;
  lastmodifiedby__name?: string | null;
  max_adults__c?: string | number | null;
  max_child_age__c?: string | null;
  max_children__c?: string | number | null;
  max_infant_age__c?: string | null;
  max_infants__c?: string | number | null;
  max_occupancy__c?: string | null;
  max_teenagers__c?: string | number | null;
  maximum_occupancy_number__c?: string | number | null;
  name?: string | null;
  number_of_rooms_connecting__c?: string | null;
  number_of_rooms_on_site__c?: string | null;
  opportunity__c?: string | null;
  prv__c?: string | number | null;
  rack_rate__c?: string | number | null;
  recordtypeid?: string | null;
  recordtype__name?: string | null;
  room_size__c?: string | number | null;
  room_view__c?: string | null;
  sfid?: string | null;
  standard_bedding_configuration__c?: string | null;
  synced_at?: string | null;
  type__c?: string | null;
};

export type OpportunityDeal = {
  allocation__c?: string | number | null;
  bed_groups?: Array<{
    configuration?: Array<{ quantity?: number; type?: string }>;
  }>;
  id: string | number;
  converted_cost__c?: string | number | null;
  cost_price__c?: string | number | null;
  cost_price_currency_price__c?: string | number | null;
  currencyisocode?: string | null;
  extra_night_margin__c?: number | null;
  fk_room_type_id?: string | null;
  included_number_of_adults__c?: string | number | null;
  included_number_of_children__c?: string | number | null;
  included_number_of_infant__c?: string | number | null;
  included_number_of_teenagers__c?: string | number | null;
  inclusions_total_value__c?: string | number | null;
  margin_check__c?: string | null;
  margin_percent__c?: string | number | null;
  max_extra_nights__c?: string | number | null;
  name?: string | null;
  number_of_nights__c?: string | number | null;
  price__c?: string | number | null;
  room_asset?: OpportunityRoomAsset | null;
  room_size__c?: string | number | null;
  sfid?: string | null;
  suggested_value__c?: number | null;
  synced_at?: string | null;
};

export type OpportunityRoom = OpportunityDeal;

export function useGetOffer(offerId: string | undefined, options?: { retain_dynamic_tags: boolean }) {
  return useQuery({
    queryKey: options ? [...offerQueryKeys.detail(offerId), options] : offerQueryKeys.detail(offerId),
    queryFn: async () => {
      const response = await OffersService.getOffer(offerId, options);
      return response.result;
    },
    enabled: !!offerId,
  });
}

export function useGetOfferSchema(offerId: string | undefined) {
  return useQuery({
    queryKey: [...offerQueryKeys.detail(offerId), 'schema'],
    queryFn: async () => {
      const response = await OffersService.getOfferSchema(offerId);
      return response.patch.body.schema;
    },
    enabled: !!offerId,
  });
}

export function useGetRoomPolicySchema(offerId: string | undefined, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...offerQueryKeys.detail(offerId), 'room-policies', 'schema'],
    queryFn: async () => {
      const response = await OffersService.getRoomPolicySchema(offerId);
      return response.schema;
    },
    enabled: enabled && !!offerId,
  });
}

export function useGetOpportunity<TOpportunity = App.OpportunityPage>(
  opportunityId: string | undefined,
  { enabled = false }: { enabled?: boolean } = {},
) {
  return useQuery<TOpportunity>({
    queryKey: offerQueryKeys.opportunity(opportunityId ?? ''),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getOpportunity(opportunityId);
      return response.result as TOpportunity;
    },
    enabled: enabled && !!opportunityId,
  });
}

export function useGetOpportunityDeals(
  opportunityId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: offerQueryKeys.opportunityDeals(opportunityId),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getDeals(opportunityId, 1, ALL_OPPORTUNITY_DEALS_FETCH_LIMIT);
      return response.result as Array<OpportunityDeal>;
    },
    enabled: enabled && !!opportunityId,
  });
}

export function useUpdateOffer(offerId: string, options?: { invalidateOnSuccess?: boolean }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      return OffersService.updateOffer(data, offerId);
    },
    onSuccess: () => {
      const shouldInvalidate = options?.invalidateOnSuccess ?? true;

      if (!shouldInvalidate) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
    },
  });
}

export interface StructuredFinePrint {
  cancellation_text: string;
  date_changes_text: string;
  inclusion_notes: string;
  fine_print: string;
}

export interface StructuredFacilityItem {
  code: string;
  name: string;
  icon?: string | null;
  displayOrder?: number | null;
}

export interface StructuredFacilityGroup {
  group: {
    code: string;
    name: string;
    icon?: string | null;
    displayOrder?: number | null;
  };
  facilities: Array<StructuredFacilityItem>;
}

export function useUpdateStructuredFinePrint(offerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StructuredFinePrint) => {
      return OffersService.updateOffer({ structured_fine_print: data }, offerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
    },
  });
}
